import { makeStyles } from "tss-react/mui";
import {
  MiFilterAccordion,
  MiFilterBreadcrumbs,
  MiFilterItem,
  MiIcon,
  MiInputTextbox,
  MiInternalDrawer,
} from "@miplatform/components";
import { ICONS_WEB } from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { Box, InputAdornment, Typography, useTheme } from "@mui/material";

export function CategoryFilterDrawer({
  open = true,
  onClose = null,
  title = "Filter",
  categoryFilterOptions,
  search,
  setSearch,
  selectedCategory,
  selectedAttributeValues,
  actions = [],
  handleCategoryClick,
  handleAttributeValueClick,
  handleReset,
  handleResetSelection,
}) {
  const { classes } = useStyles();
  const theme = useTheme();

  const isOptionSelected = (attributeId, attributeValue) =>
    selectedAttributeValues?.[attributeId]?.includes(attributeValue);

  const categoryListItem = (category) => {
    const { categoryId, categoryName } = category;
    return (
      <MiFilterItem
        key={categoryId}
        keyVal={categoryId}
        title={categoryName}
        handleClick={() => handleCategoryClick(category)}
        showIcon
        iconPath={ICONS_WEB.CHEVRON_RIGHT}
        iconColor={theme.palette.grey.main}
      />
    );
  };

  const attributeValueListItem = (attributeId, attributeValue) => {
    const isSelected = isOptionSelected(attributeId, attributeValue);
    return (
      <MiFilterItem
        key={attributeId + attributeValue}
        keyVal={attributeValue + attributeId}
        title={attributeValue}
        handleClick={() =>
          handleAttributeValueClick(attributeId, attributeValue)
        }
        typographyStyle={{ paddingLeft: theme.spacing(3) }}
        showIcon={isSelected}
        selected={isSelected}
        iconPath={ICONS_WEB.CHECK}
        iconColor={theme.palette.primary.main}
      />
    );
  };

  const attributeAccordion = (attribute) => {
    const { attributeId, attributeName, values = [] } = attribute;

    const disabled = values.length === 0;
    const selectedOptions = selectedAttributeValues?.[attributeId] ?? [];
    const children = values?.map((option) =>
      attributeValueListItem(attributeId, option)
    );

    return (
      <MiFilterAccordion
        key={attributeName + attributeId}
        keyVal={attributeId + attributeName}
        title={attributeName}
        disabled={disabled}
        selectedOptions={selectedOptions}
        showItemCount
        selectedCategory={selectedCategory}
      >
        {children}
      </MiFilterAccordion>
    );
  };

  const headerContent = () => {
    return (
      <>
        <MiInputTextbox
          className={classes.inputTextbox}
          placeholder="Search Items"
          value={search}
          handleChange={(e) => setSearch(e.target.value)}
          textfieldProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <MiIcon
                  path={mdi[ICONS_WEB.CLOSE]}
                  onClick={() => setSearch("")}
                />
              </InputAdornment>
            ),
          }}
        />
        <MiFilterBreadcrumbs
          rootTitle="Categories"
          onRootClick={handleReset}
          options={[selectedCategory?.categoryName]}
          actionIconPath={ICONS_WEB.HISTORY}
          actionIconLabel="Reset Filters"
          onActionIconClick={handleResetSelection}
        />
      </>
    );
  };

  const filterContent = () => {
    // initial state; no category selected
    if (!selectedCategory?.categoryId) {
      return categoryFilterOptions.map((category) =>
        categoryListItem(category)
      );
    }
    // category selected; show available attribute accordions
    return selectedCategory?.attributes?.length ? (
      selectedCategory?.attributes?.map((attribute) =>
        attributeAccordion(attribute)
      )
    ) : (
      <Box className={classes.emptyAttributesContainer}>
        <Typography variant="subtitle2">
          No attribute filters available.
        </Typography>
      </Box>
    );
  };

  return (
    <MiInternalDrawer
      open={open}
      title={title}
      actions={actions}
      onClose={onClose}
      className={classes.root}
      removeHeaderMargin
    >
      <Box className={classes.drawerContainer} sx={{ width: "300px" }}>
        <Box className={classes.headerContainer}>{headerContent()}</Box>
        <Box className={classes.contentContainer}>{filterContent()}</Box>
      </Box>
    </MiInternalDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    boxShadow: "-4px 0px 10px -8px rgba(0,0,0,0.6)",
  },
  drawerContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    width: "100%",
    padding: theme.spacing(1, 3),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflowY: "auto",
  },
  emptyAttributesContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing(4),
    color: theme.palette.grey.main,
  },
  actionContainer: {
    position: "sticky",
    bottom: "0",
    minHeight: 57,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "4px 16px",
    gap: 16,
    borderTop: `1px solid ${theme.palette.grey.lightAccent}`,
    boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.1)",
  },
  inputTextbox: {
    padding: theme.spacing(1, 0),
  },
}));
